import React from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PersonCell } from '../../components/ui';
import { TimeOffType } from '../../store/slices/timeOffSlice';
import type { Employee } from '../../store/slices/employeeSlice';
import { TimeOffFormData, typeLabel } from './shared';

/** Field errors from validateTimeOffRange / validateTimeOffHours (components/modals/time-off/timeOffHours.ts). */
export interface ScheduleTimeOffErrors {
  startDate?: string;
  endDate?: string;
  hours?: string;
}

interface ScheduleTimeOffDialogProps {
  open: boolean;
  employee: Employee | null;
  formData: TimeOffFormData;
  onChange: (field: keyof TimeOffFormData, value: any) => void;
  onClose: () => void;
  onSubmit: () => void;
  saving: boolean;
  /** Shown before saving when the request will land in the approval queue (see queueNoteFor). */
  queueNote?: string | null;
  /** Shown under each field; the dialog can't save a backwards range or impossible hours. */
  errors?: ScheduleTimeOffErrors;
}

/** "Schedule time off" for one employee: type, dates, hours and notes. */
const ScheduleTimeOffDialog: React.FC<ScheduleTimeOffDialogProps> = ({
  open,
  employee,
  formData,
  onChange,
  onClose,
  onSubmit,
  saving,
  queueNote,
  errors = {},
}) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="schedule-time-off-title">
      <DialogTitle id="schedule-time-off-title">Schedule time off</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
          {employee && (
            <PersonCell name={employee.name} secondary={employee.department?.name || employee.email} />
          )}
          <TextField select fullWidth label="Type" value={formData.type} onChange={(event) => onChange('type', event.target.value)}>
            {Object.values(TimeOffType).map((type) => (
              <MenuItem key={type} value={type}>
                {typeLabel(type)}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <DatePicker
              label="Start date"
              value={formData.startDate}
              onChange={(date) => onChange('startDate', date)}
              slotProps={{ textField: { fullWidth: true, error: Boolean(errors.startDate), helperText: errors.startDate } }}
            />
            <DatePicker
              label="End date"
              value={formData.endDate}
              minDate={formData.startDate ?? undefined}
              onChange={(date) => onChange('endDate', date)}
              slotProps={{ textField: { fullWidth: true, error: Boolean(errors.endDate), helperText: errors.endDate } }}
            />
          </Box>
          <TextField
            fullWidth
            label="Hours"
            type="number"
            value={Number.isNaN(formData.hours) ? '' : formData.hours}
            onChange={(event) => onChange('hours', parseInt(event.target.value, 10))}
            error={Boolean(errors.hours)}
            helperText={errors.hours ?? 'Filled in at 8h per workday from the dates; weekends and company holidays don’t count. Lower it for partial days.'}
            slotProps={{ htmlInput: { min: 1, step: 1, inputMode: 'numeric', style: { fontVariantNumeric: 'tabular-nums' } } }}
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            placeholder="e.g. Family trip, back Monday"
            multiline
            minRows={3}
            maxRows={8}
            value={formData.notes}
            onChange={(event) => onChange('notes', event.target.value)}
          />
          {queueNote && <Alert severity="info">{queueNote}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={saving} aria-busy={saving || undefined}>
          {saving ? 'Saving…' : 'Save time off'}
        </Button>
      </DialogActions>
    </Dialog>
  </LocalizationProvider>
);

export default ScheduleTimeOffDialog;
